import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ifasColors } from "./OperationsUI";

/**
 * Shared "standard" IFAS UI primitives — the agreed system-wide look:
 * tone-striped metric cards (label + tinted icon + value + caption),
 * colour-coded status chips with a leading dot, and boxy buttons.
 * Theme colours come from ifasColors; green/amber/red/blue/purple are
 * semantic status tones.
 *
 * MONEY IS NOT HERE. The one money formatter is `formatR` in
 * `src/utils/money.ts` — it emits the approved `R 12 500.00`. It lives in
 * utils rather than here because it has no React in it and because
 * OperationsUI (which owns moneyCellSx) would close an import cycle reaching
 * for it. Import it from utils; do not declare a local copy. There were nine.
 */

export type Tone = "orange" | "green" | "amber" | "red" | "blue" | "purple" | "grey" | "ink";

export const STANDARD_TONES: Record<Tone, { color: string; bg: string }> = {
  orange: { color: ifasColors.orange, bg: ifasColors.softOrange },
  green: { color: "#217A3D", bg: "#EAF7EF" },
  amber: { color: "#B45309", bg: "#FDF0DF" },
  red: { color: "#C62828", bg: "#FDECEC" },
  blue: { color: ifasColors.info, bg: "#EAF1FF" },
  purple: { color: "#6F42C1", bg: "#F1EBFF" },
  grey: { color: "#5C6472", bg: "#EEF0F3" },
  ink: { color: ifasColors.text, bg: ifasColors.page },
};

const humanizeStatus = (value?: string) =>
  String(value || "").replace(/[_-]+/g, " ").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** Derive a sensible tone from a status string by keyword. */
export const standardStatusTone = (value?: string): Tone => {
  const v = String(value || "").toUpperCase();
  if (/COMPLETE|CLOSED|PAID|APPROVED|RELEASED|DELIVERED|CONFIRMED|READY|COLLECTED|ISSUED|SIGNED/.test(v)) return "green";
  if (/CANCEL|DECLIN|REJECT|FAIL|OUT[_ ]?OF[_ ]?STOCK|OVERDUE|ERROR/.test(v)) return "red";
  if (/AWAIT|PENDING|DRAFT|DOCUMENTS|HOLD|REVIEW|REQUEST|FEEDBACK|PLANNING/.test(v)) return "amber";
  if (/APPROVAL/.test(v)) return "blue";
  if (/PROGRESS|EXECUTION|ACTIVE|ROUTE|DISPATCH|TRANSFER/.test(v)) return "blue";
  if (/ALLOCATED|ASSIGNED|OFFERED/.test(v)) return "purple";
  return "grey";
};

/**
 * Three-way stock status vocabulary — Warehouse is the single source of truth.
 * Derived presentationally from availableQty and the server `lowStock` flag:
 * availableQty === 0 -> Out of Stock (red), lowStock -> Low Stock (amber),
 * else Normal (green). Quotations, Catering and Tombstones must consume this
 * helper (and/or StockStatusChip below) rather than deriving their own tones.
 */
export type StockStatusValue = { label: "Out of Stock" | "Low Stock" | "Normal"; tone: Tone };

export function deriveStockStatus(availableQty?: number | null, lowStock?: boolean | null): StockStatusValue {
  const qty = Number(availableQty ?? 0);
  if (qty === 0) return { label: "Out of Stock", tone: "red" };
  if (lowStock) return { label: "Low Stock", tone: "amber" };
  return { label: "Normal", tone: "green" };
}

export function StockStatusChip({ availableQty, lowStock }: { availableQty?: number | null; lowStock?: boolean | null }) {
  const s = deriveStockStatus(availableQty, lowStock);
  return <StatusDotChip status={s.label} tone={s.tone} />;
}

/**
 * `compact` is additive and defaults to the chip every existing call site
 * already renders. `compact` is the approved operations-queue mock's `.chip`
 * — 11.2px label, 6.4px dot gap, 23px tall — sized to sit in a 9.6px-padded
 * queue row without stretching it.
 */
export function StatusDotChip({ status, tone, compact = false }: { status?: string; tone?: Tone; compact?: boolean }) {
  const t = STANDARD_TONES[tone ?? standardStatusTone(status)];
  return (
    <Chip
      size="small"
      label={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: compact ? 0.8 : 0.6 }}>
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: "999px", bgcolor: "currentColor", flexShrink: 0 }} />
          {humanizeStatus(status)}
        </Box>
      }
      sx={{
        color: t.color,
        bgcolor: t.bg,
        fontWeight: 800,
        borderRadius: "8px",
        ...(compact ? { height: 23, fontSize: "11.2px", "& .MuiChip-label": { px: 1 } } : null),
      }}
    />
  );
}

/**
 * `onClick` / `selected` are additive and default to the static card every
 * existing call site already renders. Supplying `onClick` turns the card into a
 * real `<button>` that toggles a filter — the approved operations-queue mock's
 * `.mcard`, where "the cards ARE the filter" and `.mcard.on` is a 2px inset
 * tone ring. Passing `selected` without `onClick` is meaningless and is
 * ignored for the pressed state.
 */
export function StandardMetricCard({
  label,
  value,
  caption,
  tone = "orange",
  icon,
  compact = false,
  onClick,
  selected = false,
}: {
  label: string;
  value: React.ReactNode;
  caption?: string;
  tone?: Tone;
  icon?: React.ReactNode;
  compact?: boolean;
  onClick?: () => void;
  selected?: boolean;
}) {
  const t = STANDARD_TONES[tone];
  const interactive = typeof onClick === "function";
  return (
    <Paper
      elevation={0}
      {...(interactive
        ? { component: "button" as const, type: "button" as const, onClick, "aria-pressed": selected }
        : null)}
      sx={{
        position: "relative",
        height: "100%",
        ...(interactive
          ? {
              width: "100%",
              display: "block",
              textAlign: "left",
              font: "inherit",
              cursor: "pointer",
              transition: "box-shadow .15s, border-color .15s",
              "&:focus-visible": { outline: `2px solid ${ifasColors.orange}`, outlineOffset: "2px" },
            }
          : null),
        ...(interactive && selected
          ? { borderColor: t.color, boxShadow: `0 0 0 2px ${t.color} inset` }
          : null),
        p: compact ? "13px 15px" : "16px 18px",
        pl: compact ? "16px" : "19px",
        borderRadius: compact ? "13px" : "14px",
        border: `1px solid ${ifasColors.border}`,
        bgcolor: ifasColors.surface,
        boxShadow: "0 4px 14px rgba(20,24,31,.05)",
        overflow: "hidden",
        "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 4, backgroundColor: t.color },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        {/*
          Uppercase is the SYSTEM standard, from the gold reference mock:
          special-services-mock.html `.kpi-label{font-size:11px;font-weight:800;
          letter-spacing:.5px;text-transform:uppercase;color:var(--muted)}`.
          The operations-queue mock's `.mc-label` is title case at .72rem — it is
          the outlier, and it loses to the gold reference.

          Known cost, accepted 2026-09-21 (Tony, ruling A6): uppercase +0.4px
          tracking makes a label ~15-20% wider than the same string in title
          case, so in the mock's 2-up sub-`sm` grid at 375px, long labels
          ("Transfer In Progress", "Released / Delivered") wrap to 2-3 lines.
          That is accepted. Do NOT "fix" it with noWrap: truncating to
          "Transfer In Prog…" destroys meaning, where wrapping only costs
          height. Cards are grid-stretched, so a wrapped pair stays aligned.
        */}
        <Typography sx={{ fontSize: compact ? "10.5px" : "11px", fontWeight: 800, letterSpacing: "0.4px", textTransform: "uppercase", color: ifasColors.muted, lineHeight: 1.3 }}>
          {label}
        </Typography>
        {icon && (
          <Box sx={{ width: compact ? 26 : 30, height: compact ? 26 : 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: t.bg, color: t.color, flexShrink: 0, "& .MuiSvgIcon-root": { fontSize: compact ? 16 : 18 } }}>
            {icon}
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: compact ? "26px" : "30px", fontWeight: 900, letterSpacing: "-.02em", mt: 1, mb: caption ? 0.25 : 0, lineHeight: 1, color: ifasColors.text }}>
        {value}
      </Typography>
      {caption && <Typography sx={{ fontSize: "12px", fontWeight: 600, color: ifasColors.muted }}>{caption}</Typography>}
    </Paper>
  );
}

/* Boxy button sx tokens (rounded-rectangle, not pills). */
export const standardPrimaryBtnSx = {
  borderRadius: "10px", px: 1.9, minHeight: 40, textTransform: "none", fontWeight: 800, whiteSpace: "nowrap",
  background: ifasColors.orange, bgcolor: ifasColors.orange, color: "#FFFFFF", boxShadow: "0 8px 18px rgba(236,110,0,.22)",
  "&:hover": { background: ifasColors.deepOrange, bgcolor: ifasColors.deepOrange },
};
export const standardGhostBtnSx = {
  borderRadius: "10px", px: 1.9, minHeight: 40, textTransform: "none", fontWeight: 800, whiteSpace: "nowrap",
  bgcolor: ifasColors.surface, color: ifasColors.text, border: `1px solid ${ifasColors.border}`,
  "&:hover": { borderColor: ifasColors.muted, bgcolor: ifasColors.softOrange },
};
export const standardOutlineBtnSx = {
  borderRadius: "10px", px: 1.9, minHeight: 40, textTransform: "none", fontWeight: 800, whiteSpace: "nowrap",
  bgcolor: ifasColors.surface, color: ifasColors.deepOrange,
  border: `1px solid ${ifasColors.orange}55`,
  "&:hover": { bgcolor: ifasColors.softOrange, borderColor: ifasColors.orange },
};

/** Standard table row: roomy cells on a single line, light divider, soft-orange hover. */
export const standardTableRowSx = {
  "& .MuiTableCell-root": { py: 1.85, whiteSpace: "nowrap", borderBottom: `1px solid ${ifasColors.border}` },
  "&:last-of-type .MuiTableCell-root": { borderBottom: "none" },
  "&:hover": { backgroundColor: `${ifasColors.softOrange} !important` },
};

/** Airy table-header cell styling (light muted uppercase) for the standard tables. */
export const standardTableHeadCellSx = {
  fontSize: "11px", fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase", whiteSpace: "nowrap",
  color: ifasColors.muted, bgcolor: ifasColors.page, borderBottom: `1px solid ${ifasColors.border}`, py: 1.5,
};

/**
 * Small count badge (peach pill) shown next to a table/section title.
 *
 * `shape` is additive and defaults to the boxy badge every existing call site
 * already renders. `shape="pill"` is the approved operations-queue mock's
 * `.badge` — a true 999px pill at 11.5px — used by `StandardListCardHead`.
 * Adding a variant here rather than a second badge component keeps one count
 * badge in the system.
 */
export function StandardCountBadge({ count, shape = "boxy" }: { count: number | string; shape?: "boxy" | "pill" }) {
  const pill = shape === "pill";
  return (
    <Box
      sx={{
        minWidth: pill ? 0 : 26,
        height: pill ? "auto" : 24,
        px: pill ? "8.8px" : 1,
        py: pill ? "1.92px" : 0,
        borderRadius: pill ? "999px" : "8px",
        bgcolor: ifasColors.softOrange,
        color: ifasColors.orange,
        fontSize: pill ? "11.5px" : 12,
        fontWeight: pill ? 800 : 900,
        lineHeight: pill ? 1.5 : undefined,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {count}
    </Box>
  );
}

/** Standard table footer pager — "Showing X–Y of N" + numbered page buttons. */
export function StandardPager({
  page,
  total,
  pageSize = 10,
  onPage,
  captionSuffix,
}: {
  page: number;
  total: number;
  pageSize?: number;
  onPage: (p: number) => void;
  /** Optional text appended after the "Showing X–Y of N" caption, e.g. "· latest 50 of each status". */
  captionSuffix?: string;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const start = total === 0 ? 0 : current * pageSize + 1;
  const end = Math.min((current + 1) * pageSize, total);
  const nums = pageCount <= 7
    ? Array.from({ length: pageCount }, (_, i) => i)
    : Array.from(new Set([0, 1, current - 1, current, current + 1, pageCount - 2, pageCount - 1].filter((n) => n >= 0 && n < pageCount))).sort((a, b) => a - b);
  const btn = (label: React.ReactNode, opts: { active?: boolean; disabled?: boolean; onClick?: () => void }) => (
    <Box
      component="button"
      disabled={opts.disabled}
      onClick={opts.disabled ? undefined : opts.onClick}
      sx={{
        minWidth: 30, height: 30, px: 0.75, borderRadius: "8px", fontFamily: "inherit", fontSize: "12.5px", fontWeight: 800,
        cursor: opts.disabled ? "default" : "pointer", opacity: opts.disabled ? 0.4 : 1,
        border: `1px solid ${opts.active ? "transparent" : ifasColors.border}`,
        bgcolor: opts.active ? ifasColors.orange : ifasColors.surface,
        color: opts.active ? "#FFFFFF" : ifasColors.text,
        "&:hover": { bgcolor: opts.active ? ifasColors.orange : ifasColors.softOrange },
      }}
    >
      {label}
    </Box>
  );
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", p: 2, borderTop: `1px solid ${ifasColors.border}`, bgcolor: ifasColors.page }}>
      <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: ifasColors.muted }}>
        {total === 0
          ? "No records to show"
          : `Showing ${start}–${end} of ${total}${captionSuffix ? ` ${captionSuffix}` : ""}`}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
        {btn("‹", { disabled: current === 0, onClick: () => onPage(current - 1) })}
        {nums.map((n, i) => (
          <React.Fragment key={n}>
            {i > 0 && n - nums[i - 1] > 1 && <Box component="span" sx={{ px: 0.25, color: ifasColors.muted, fontWeight: 800 }}>{"…"}</Box>}
            {btn(n + 1, { active: n === current, onClick: () => onPage(n) })}
          </React.Fragment>
        ))}
        {btn("›", { disabled: current >= pageCount - 1, onClick: () => onPage(current + 1) })}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ *
 * Operations list-card family
 *
 * Built from `.claude/agent-team/mocks/approved/operations-queue-mock.html`,
 * measured off its own CSS rather than off a sibling module. Every value below
 * is the mock's value converted at a 16px root (`.6rem` -> 9.6px -> `py: 1.2`).
 *
 * ADDITIVE ONLY. Nothing above this comment changed behaviour: `StandardUI`
 * has 8 importers and the live claims tree consumes it via
 * `claims/common/History.tsx`. These are new exports that sit beside the old
 * ones; the old ones keep serving the modules that already ship them.
 * ------------------------------------------------------------------ */

/**
 * The list panel.
 *
 * Use INSTEAD OF `operationsTablePaperSx` for these screens. That token carries
 * `"& .MuiTableCell-root": { fontSize: 13.5 }`, a descendant rule at
 * specificity (0,2,0) that out-specifies any cell-level sx at (0,1,0) — which
 * is why `standardListHeadCellSx` silently does nothing inside it. This token
 * deliberately styles only the panel, so cell-level tokens apply.
 *
 * Mock: `.panel` — radius 16, 1px border, `0 4px 14px rgba(20,24,31,.05)`.
 */
export const standardListCardSx = {
  bgcolor: ifasColors.surface,
  border: `1px solid ${ifasColors.border}`,
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 4px 14px rgba(20,24,31,.05)",
};

/** Horizontal scroll wrapper for the table. Mock: `.tscroll` + thin thumb. */
export const standardListScrollSx = {
  overflowX: "auto",
  "&::-webkit-scrollbar": { height: 8 },
  "&::-webkit-scrollbar-thumb": { backgroundColor: ifasColors.border, borderRadius: "8px" },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
};

/**
 * Table head cell for the list card. Mock: `.tbl th` — 10.1px / 800 / .09em
 * uppercase muted on `page`, 9.6px x 13.6px padding, `border` underline.
 *
 * Spread from `standardTableHeadCellSx` so the lineage is explicit; it differs
 * only where the mock differs (font 10.1 not 11, tracking .09em not 0.6px,
 * padding 9.6 not 12). The base token is frozen, so this is the queue's copy.
 */
export const standardListHeadCellSx = {
  ...standardTableHeadCellSx,
  fontSize: "10.1px",
  letterSpacing: "0.09em",
  py: 1.2,
  px: 1.7,
};

/**
 * Body row for the list card: clickable, keyboard-reachable, at the mock's
 * density.
 *
 * DENSITY IS THE POINT — and the two row tokens are CORRECTLY divergent,
 * because they implement two different approved contracts. Do not "unify" them.
 *
 * Re-derived 2026-09-21 (Tony, ruling A4), because the previous version of this
 * comment framed the split as unreconciled debt and gave a false reason for it:
 *
 *   operations-queue-mock.html  `.tbl td{padding:.6rem .85rem}`  =  9.6px
 *     -> standardClickableRowSx  py: 1.2  =  9.6px   (delta 0.0px)
 *   special-services-mock.html  `tbody td{padding:15px 16px}`    = 15.0px
 *     -> standardTableRowSx      py: 1.85 = 14.8px   (delta 0.2px)
 *   reports-mock.html           `tbody td{padding:16px 18px}`    = 16.0px
 *     -> standardTableRowSx      py: 1.85 = 14.8px   (delta 1.2px)
 *
 * So `standardTableRowSx` is NOT "54% too tall". It is within 0.2px of the gold
 * reference mock (special-services) that it actually serves. The old comment
 * measured it against the operations-queue mock, which was never its contract.
 *
 * The old comment also claimed the token is rendered by "the claims history
 * tab". It is not: claims/common/History.tsx imports only from OperationsUI,
 * never from StandardUI. The real consumer set is 6 IFAS modules + this file
 * (Catering, Fleet, CasePortal, SpecialServices, Tombstone, Warehouse).
 *
 * Repainting those 6 to 9.6px would move Special Services AWAY from its own
 * approved mock. That is why there is no unification ticket.
 *
 * Two other deliberate divergences from `standardTableRowSx`, both the mock's:
 * the divider is `ifasColors.rule` not `border`, and hover is `ifasColors.page`
 * not soft orange.
 *
 * Cells carry the mock's 13.4px base. A bare `<Typography>` inside a cell will
 * still render at the theme's `body1` 16px and break the row — use
 * `standardListCellPrimarySx` / `standardListCellSecondarySx` for cell text.
 */
export const standardClickableRowSx = {
  cursor: "pointer",
  "& .MuiTableCell-root": {
    py: 1.2,
    px: 1.7,
    fontSize: "13.4px",
    color: ifasColors.text,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${ifasColors.rule}`,
  },
  "&:last-of-type .MuiTableCell-root": { borderBottom: "none" },
  "&:hover": { backgroundColor: ifasColors.page },
  "&:focus-visible": {
    outline: `2px solid ${ifasColors.orange}`,
    outlineOffset: "-2px",
    backgroundColor: ifasColors.page,
  },
};

/** Primary line of a two-line cell. Mock: `.p1` — 13.4px / 900 / ink. */
export const standardListCellPrimarySx = {
  display: "block",
  fontSize: "13.4px",
  fontWeight: 900,
  lineHeight: 1.35,
  color: ifasColors.text,
};

/** Secondary line of a two-line cell. Mock: `.p2` — 11.2px / 600 / muted. */
export const standardListCellSecondarySx = {
  display: "block",
  fontSize: "11.2px",
  fontWeight: 600,
  lineHeight: 1.35,
  color: ifasColors.muted,
  mt: "2.4px",
};

/**
 * Head of a list card: title + count. Mock: `.phead` / `.ptitle` / `.badge`.
 * The count is MANDATORY and must be the server total, not `rows.length`.
 *
 * There is no actions slot, because the mock's panel head has no actions.
 */
export function StandardListCardHead({ title, count }: { title: string; count: number | string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.4,
        py: 2.1,
        px: 2.5,
        borderBottom: `1px solid ${ifasColors.border}`,
      }}
    >
      <Typography component="h2" sx={{ fontSize: "17px", fontWeight: 800, letterSpacing: "-0.01em", color: ifasColors.text }}>
        {title}
      </Typography>
      <StandardCountBadge count={count} shape="pill" />
    </Box>
  );
}

/**
 * Row action button. Mock: `.btn-o` — NEUTRAL (ink on surface, `border`
 * hairline, grey hover), NOT the orange-tinted `standardOutlineBtnSx`. This
 * supersedes the line in the pattern doc that reused that token; the mock is
 * the contract and the mock's row button is not orange.
 *
 * MUST be used with `variant="text"` or no variant. `theme.ts:140-154` repaints
 * every `variant="outlined"` Button `#333333` with white text and no border
 * while `REACT_APP_UI_STANDARD` is unset — and it is set in no `.env` file, so
 * it is live. The theme flag is app-global and shared with the live claims
 * screens; it is not flipped to make these two modules look right. Both tokens
 * below also set `fontSize` and `lineHeight` explicitly because
 * `MuiButton.styleOverrides.root` otherwise imposes 16px/30px on every button.
 */
export const standardRowActionBtnSx = {
  minHeight: 34,
  py: 0.675,
  px: 1.6,
  borderRadius: "10px",
  textTransform: "none",
  fontSize: "11.8px",
  fontWeight: 700,
  lineHeight: 1.4,
  whiteSpace: "nowrap",
  bgcolor: ifasColors.surface,
  color: ifasColors.text,
  border: `1px solid ${ifasColors.border}`,
  "&:hover": { bgcolor: STANDARD_TONES.grey.bg, borderColor: ifasColors.border },
};

/**
 * Page-header secondary button (Refresh, Back). Mock: `.btn-g` — transparent,
 * muted label, hairline border, 12.5px/700. Same `variant="text"` rule as
 * `standardRowActionBtnSx`.
 */
export const standardListGhostBtnSx = {
  minHeight: 35,
  py: 1,
  px: 1.6,
  gap: 0.7,
  borderRadius: "10px",
  textTransform: "none",
  fontSize: "12.5px",
  fontWeight: 700,
  lineHeight: 1.4,
  whiteSpace: "nowrap",
  bgcolor: "transparent",
  color: ifasColors.muted,
  border: `1px solid ${ifasColors.border}`,
  "&:hover": { bgcolor: ifasColors.page, borderColor: ifasColors.muted },
};

/**
 * Shared soft-orange cross-link idiom — a boxy peach chip-link, NOT a
 * neutral row-action button. Reproduces the approved Fleet mocks' `.manage`
 * (`fleet-portal-vehicles.html`/`fleet-portal-drivers.html`), `.qlink`
 * (`fleet-portal-drivers.html`), `.arow-link` (`fleet-portal-alerts.html`)
 * and `.rwatch-link` (`fleet-portal-assignments.html`) — four slightly
 * different soft-orange link treatments in the mocks, unified here into one
 * reusable token so every "Manage → register" / "View in X →" / "View
 * driver/vehicle →" control reads as the same link idiom wherever it
 * appears. Use with `<Button variant="text">` (or no variant) — never for a
 * genuine action button, which stays on `standardRowActionBtnSx`.
 */
export const standardManageLinkSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.6,
  fontWeight: 800,
  fontSize: "12.5px",
  color: ifasColors.deepOrange,
  bgcolor: ifasColors.softOrange,
  border: `1px solid ${alpha(ifasColors.orange, 0.32)}`,
  borderRadius: "9px",
  px: 1.4,
  py: 0.8,
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const,
};

/**
 * Small bordered status tile: a coloured dot + uppercase label on top, a big
 * number below. Presentational only — no owning-module business logic.
 * Matches the approved Fleet Vehicles/Drivers mocks' `.stile`, promoted here
 * because it is pure presentation reused across tabs (not Fleet-specific).
 */
export function StandardCompactStatusTile({
  label,
  value,
  tone,
  alert = false,
}: {
  label: string;
  value: number | string;
  tone: Tone;
  alert?: boolean;
}) {
  const t = STANDARD_TONES[tone];
  return (
    <Box
      sx={{
        border: `1px solid ${alert ? "transparent" : ifasColors.border}`,
        borderRadius: "10px",
        p: "7px 10px",
        bgcolor: alert ? t.bg : ifasColors.surface,
      }}
    >
      <Box display="flex" alignItems="center" gap={0.75}>
        {!alert && (
          <Box sx={{ width: 7, height: 7, borderRadius: "999px", flexShrink: 0, bgcolor: t.color }} />
        )}
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            color: alert ? t.color : ifasColors.muted,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 900, color: alert ? t.color : ifasColors.text, mt: 0.4, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Compact label/value detail card — a bordered box with an uppercase title
 * and a stack of `label: value` rows, each row divided by a hairline except
 * the last. Presentational only — no owning-module business logic. Matches
 * the approved Fleet mocks' `.dcard`, promoted here because it is pure
 * presentation reused across expandable-row detail panels.
 */
export function StandardDetailCard({ title, rows }: { title: string; rows: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <Box sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: "10px", bgcolor: ifasColors.surface, p: "9px 11px" }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.4px", textTransform: "uppercase", color: ifasColors.deepOrange, mb: 0.6 }}>
        {title}
      </Typography>
      <Box>
        {rows.map((row, idx) => (
          <Box
            key={row.label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1.25,
              fontSize: 12,
              py: "3px",
              borderBottom: idx === rows.length - 1 ? "none" : `1px solid ${ifasColors.border}`,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: ifasColors.muted, flexShrink: 0 }}>{row.label}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: ifasColors.text, textAlign: "right" }}>{row.value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * The operations status vocabulary, bound to a tone explicitly.
 *
 * It exists because inference (`standardStatusTone`) is what produces the grey
 * status column, and correcting the inference itself would repaint Warehouse
 * and Special Services. An explicit map changes nothing for anyone else and
 * gives these screens the exact tones that were approved.
 *
 * PROVENANCE — corrected 2026-09-21 (Tony, ruling A3).
 * An earlier revision of this comment, and the message of commit `ea4c5a9`,
 * both claimed these values were "the approved values from
 * operations-queue-pattern.md". That citation is false: that document contains
 * no status->tone table. (Its sections B and D say "Tones per section E" /
 * "see E", but section E is "Detail view" — the cross-reference is dangling and
 * the table it promises was never written.)
 *
 * The values were in fact carried over verbatim from the `feat/ifas-reports-declutter`
 * branch's copy of this file, where they were authored. They are byte-identical
 * to it. Treat them as the working vocabulary, NOT as client-approved values —
 * only the entries below that cite a specific mock line have been verified
 * against an approved mock.
 */
export const OPERATIONS_STATUS_TONES: Record<string, Tone> = {
  // Mortuary record
  PENDING_INTAKE: "amber",
  INTAKE_REGISTERED: "blue",
  BODY_CONTROL_ACTIVE: "blue",
  TRANSFER_IN_PROGRESS: "blue",
  RELEASED_TO_EXTERNAL_PARLOUR: "green",
  DELIVERED_TO_FAMILY: "green",
  // Mortuary register capacity.
  // AVAILABLE is CROSS-MODULE, verified 2026-09-21 (Tony, ruling A2). It is read
  // by Mortuary capacity AND by Fleet's vehicle/driver chips
  // (FleetPortal.tsx:1776, 1801, 1949 via operationsStatusTone).
  // Before this key existed, "AVAILABLE" missed every branch of
  // `standardStatusTone` and fell through to its `return "grey"` default, so
  // Fleet painted Available chips grey. The approved mocks say green:
  // fleet-portal-vehicles.html:172 and fleet-portal-drivers.html:163 both use
  // `background:var(--green)`, and fleet-portal-drivers.html:184 uses `chip g`.
  // So green makes Fleet CONFORM to its mock; it is a contract fix, not a
  // regression. Do not "restore" grey.
  AVAILABLE: "green",
  FULL: "red",
  SETUP_NEEDED: "amber",
  SETUP_REQUIRED: "amber",
  PENDING_SETUP: "amber",
  // Dispatch
  DRAFT: "amber",
  PENDING_SUBMISSION: "amber",
  TRANSFER_INITIATED: "blue",
  TRANSFER_ACCEPTED: "blue",
  EXTERNAL_RELEASE_PENDING: "amber",
  FAMILY_DELIVERY_PENDING: "amber",
  CANCELLED: "red",
  // Vehicle
  ALLOCATED: "purple",
  DISPATCHED: "blue",
  UNDER_MAINTENANCE: "amber",
  // grey, not red: fleet-portal-vehicles.html:177 renders Out of Service as
  // `background:var(--grey)`. Red is reserved for failure/cancellation in this
  // vocabulary (CANCELLED, FULL); a vehicle withdrawn from service is an
  // inactive state, the same class as UNAVAILABLE / OFF_DUTY below.
  OUT_OF_SERVICE: "grey",
  // Driver
  ASSIGNED: "purple",
  IN_SERVICE: "blue",
  UNAVAILABLE: "grey",
  OFF_DUTY: "grey",
  // Terminal exit, NOT the same class as UNAVAILABLE / OFF_DUTY / OUT_OF_SERVICE
  // above (Tony, ruling F2-A, 2026-09-21). Those three are reversible-inactive and
  // stay grey. TERMINATED and REMOVED are irreversible: DriverServiceImpl
  // .validateDriverStatusTransition (:415,:419) hard-rejects any move back to an
  // active status, and the Vehicles side rejects REMOVED the same way. Red is the
  // failure/termination class (CANCELLED, FULL), and an irreversible exit belongs
  // in it. Matches fleet-drivers-mock.html:222 (`chip r2`) and its red "Exited" KPI
  // tile (:151-153). Additive: verified 2026-09-21 that no caller of
  // operationsStatusTone passes either key today, so this changes no existing
  // rendering. Nothing in the product SETS these — they are display/count only.
  TERMINATED: "red",
  REMOVED: "red",
  // Fleet event
  ON_TIME: "green",
  AT_RISK: "amber",
  DELAYED: "amber",
  COMPLETED: "green",
};

/**
 * Tone for an operations status. Looks the status up in the approved map and
 * only falls back to `standardStatusTone` for something the map has not seen.
 * Prefer passing `tone` at the call site where the value is known.
 */
export const operationsStatusTone = (value?: string): Tone => {
  const key = String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  return OPERATIONS_STATUS_TONES[key] ?? standardStatusTone(value);
};

/**
 * A labelled horizontal tab bar — `.maintabs` / `.maintab` in
 * `fleet-portal-shell.html`: text label, optional count badge, active tab in
 * deep orange with an orange underline. Additive, standalone from `ClaimTabs`
 * (that component is shared elsewhere and is not touched here).
 *
 * Controlled: pass `value` + `onChange`. `count` renders as an inline amber
 * boxy badge matching the mock's `.tb` — amber-on-amber-bg, 6px radius, NOT
 * `StandardCountBadge` (that component's boxy variant is orange, and its pill
 * variant is round; `.tb` is neither, so it is rendered here directly rather
 * than adding a third variant to a component six other modules already ship).
 * This component is Fleet-only, so the badge stays Fleet-scoped.
 */
export type StandardLabelledTab<T extends string = string> = {
  value: T;
  label: string;
  count?: number | string;
};

export function StandardLabelledTabs<T extends string = string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<StandardLabelledTab<T>>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <Box
      role="tablist"
      sx={{
        display: "flex",
        gap: 0.75,
        flexWrap: "wrap",
        borderBottom: `1px solid ${ifasColors.border}`,
        mb: 1.6,
      }}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <Box
            key={tab.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            sx={{
              position: "relative",
              fontFamily: "inherit",
              fontSize: "13px",
              fontWeight: 800,
              color: active ? ifasColors.deepOrange : ifasColors.muted,
              bgcolor: "transparent",
              border: "none",
              borderBottom: `2.5px solid ${active ? ifasColors.orange : "transparent"}`,
              px: 1.75,
              py: 1.25,
              pb: 1.6,
              mb: "-1px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.875,
              "&:hover": { color: active ? ifasColors.deepOrange : ifasColors.text },
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <Box
                component="span"
                sx={{
                  fontWeight: 900,
                  fontSize: "10.5px",
                  bgcolor: STANDARD_TONES.amber.bg,
                  color: STANDARD_TONES.amber.color,
                  borderRadius: "6px",
                  px: "6px",
                  py: "1px",
                  minWidth: "17px",
                  textAlign: "center",
                }}
              >
                {tab.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
